import axios from 'axios';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { getParticipantsByState } from 'modules/campaigns/reducers/campaignParticipants';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { SUBCAMPAIGN_STATE_ALL, SUBCAMPAIGN_STATE_REVOKED } from 'utils/subcampaign';

import type {
  Action,
  Dispatch,
  StatisticsCommon,
  SubcampaignInfluencerProfile,
  ThunkAction
} from 'types';

const { LISTS, SUBCAMPAIGNS } = API.END_POINTS;

const CUSTOMIZABLE_AUDIENCE_OVERLAP_LOADED = 'statistics/CUSTOMIZABLE_AUDIENCE_OVERLAP_LOADED';
const CUSTOMIZABLE_AUDIENCE_OVERLAP_RESET = 'statistics/CUSTOMIZABLE_AUDIENCE_OVERLAP_RESET';

const SET_CUSTOMIZABLE_AUDIENCE_OVERLAP_STATUS = 'statistics/SET_CUSTOMIZABLE_AUDIENCE_OVERLAP_STATUS';

const CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING = 'statistics/CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING';
const CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADED = 'statistics/CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADED';

export const AUDIENCE_OVERLAP_STATUSES = {
  LOADING: 'loading',
  AVAILABLE: 'available',
  NOT_AVAILABLE: 'general_service_error'
};


const initialState = {
  creator: {
    absolute: 0,
    percentage: 0
  },
  others: {
    absolute: 0,
    percentage: 0
  },
  overlap: {
    absolute: 0,
    percentage: 0
  },
  influencers: [],
  isLoadingInfluencers: true,
  overlapStatus: AUDIENCE_OVERLAP_STATUSES.LOADING
};

const setLoadingStatus = (type: string, status: boolean): Action => ({
  type,

  payload: {
    status
  }
});

const setOverlapStatus = (status: string): Action => ({
  type: SET_CUSTOMIZABLE_AUDIENCE_OVERLAP_STATUS,

  payload: {
    status
  }
});


const dataLoaded = (
  data: {
    creator: StatisticsCommon,
    others: StatisticsCommon,
    overlap: StatisticsCommon
  }
): Action => ({
  type: CUSTOMIZABLE_AUDIENCE_OVERLAP_LOADED,

  payload: {
    data
  }
});

const influencersLoaded = (influencers: Array<SubcampaignInfluencerProfile>): Action => ({
  type: CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADED,

  payload: {
    influencers
  }
});

export const resetCustomizableAudienceOverlap = (): Action => ({
  type: CUSTOMIZABLE_AUDIENCE_OVERLAP_RESET
});

let fetchCustomizableAudienceOverlapCancelToken;

const fetchCustomizableAudienceOverlap = (url: string): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setOverlapStatus(AUDIENCE_OVERLAP_STATUSES.LOADING));


  if (fetchCustomizableAudienceOverlapCancelToken) {
    fetchCustomizableAudienceOverlapCancelToken.cancel();
  }

  fetchCustomizableAudienceOverlapCancelToken = axios.CancelToken.source();


  try {
    const response = await get(url, { cancelToken: fetchCustomizableAudienceOverlapCancelToken.token }, { suppressGlobalNotification: true });
    const data = response.data;

    dispatch(dataLoaded(data));
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    if (e.response?.data?.code) {
      dispatch(setOverlapStatus(e.response.data.code));
    } else {
      // unknown error occured
      dispatch(setOverlapStatus(AUDIENCE_OVERLAP_STATUSES.NOT_AVAILABLE));
    }

    logError(e);
  }
};

// Lists
export const fetchAllInfluencersInList = (orgId: number, listId: number): ThunkAction => {
  const params = {
    page: 1,
    items_per_page: 500,
    sort: 'followers.desc'
  };

  const queryString = `${Querystring.objectToString(params)}`;
  const url = `${LISTS.getListInfluencers(orgId, listId)}?${queryString}`;

  return async (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING, true));
    try {
      const response = await get(url);
      const influencers = (response.data.data || []).filter(influencer => influencer.channels.instagram);

      dispatch(influencersLoaded(influencers));
      dispatch(setLoadingStatus(CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING, false));

      return influencers;
    } catch (e) {
      dispatch(setLoadingStatus(CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING, false));
      logError(e);

      return e;
    }
  };
};

export const fetchCustomizableAudienceOverlapCampaign = (campaignId: number, offerState: string, influencerId: number): ThunkAction => fetchCustomizableAudienceOverlap(SUBCAMPAIGNS.getCustomizableAudienceOverlap(campaignId, offerState, influencerId));

export const fetchCustomizableAudienceOverlapLists = (orgId: number, listId: number, influencerId: number): ThunkAction => fetchCustomizableAudienceOverlap(LISTS.getCustomizableAudienceOverlap(orgId, listId, influencerId));

// Campaign overview
export const fetchAllInfluencersInCampaign = (campaignId: number, state: string): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(setLoadingStatus(CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING, true));
  try {
    const stateFilter = state === SUBCAMPAIGN_STATE_ALL ? {} : { states: state };
    const subcampaignsByState = await getParticipantsByState(campaignId, stateFilter);

    const nonRevokedSubcampaigns = subcampaignsByState.filter(subcampaign => (
      subcampaign.state !== SUBCAMPAIGN_STATE_REVOKED
    ));

    const influencers = nonRevokedSubcampaigns
      .map(({ creatorProfile }) => creatorProfile)
      .filter(creatorProfile => creatorProfile.channels.instagram);

    dispatch(influencersLoaded(influencers));
    dispatch(setLoadingStatus(CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING, false));

    return influencers;
  } catch (e) {
    dispatch(setLoadingStatus(CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING, false));
    logError(e);

    return e;
  }
};

export default createReducer<any, any>('statisticsCustomizableAudienceOverlap', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADING]: (state, { status }) => ({
    ...state,
    isLoadingInfluencers: status
  }),
  [CUSTOMIZABLE_AUDIENCE_INFLUENCERS_LOADED]: (state, { influencers }) => ({
    ...state,
    influencers
  }),
  [SET_CUSTOMIZABLE_AUDIENCE_OVERLAP_STATUS]: (state, { status }) => ({
    ...state,
    overlapStatus: status
  }),
  [CUSTOMIZABLE_AUDIENCE_OVERLAP_LOADED]: (state, { data }) => ({
    ...state,
    ...data,
    overlapStatus: AUDIENCE_OVERLAP_STATUSES.AVAILABLE
  }),

  [CUSTOMIZABLE_AUDIENCE_OVERLAP_RESET]: () => initialState
});
